import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Typography } from "src/assets/imports/antdComponents";
import DynamicLogo from "src/components/DynamicLogo";
import { fetchAuthSession } from "aws-amplify/auth";
import { clearCognitoToken } from "src/services/cognitoAuth";
import {
	resumePendingCognitoLogin,
	consumeSignedOutFlag,
	startInteractiveCognitoLogin,
} from "src/services/cognitoAutoLogin";
import { environment } from "src/config/environment";
import { isCognitoLoginAllowed, isIdmLoginAllowed, isOnlyIdmAllowed, isAllowedDomain, resolveProviderForEmail } from "src/config/loginMethods";
import { getRememberedUser, clearRememberedUser } from "src/utils/rememberedUser";
import "./style.css";

type LoginState = "loading" | "remembered" | "fresh";

const validateEmail = (email: string): boolean => {
	if (!email) return false;
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email.trim());
};

const isSafeNavigationUrl = (value: string | undefined): boolean => {
	if (!value) return false;
	try {
		const protocol = new URL(value).protocol;
		return protocol === "https:" || protocol === "http:";
	} catch {
		return false;
	}
};

const Login: React.FC = () => {
	const { t } = useTranslation();
	const [isSignedOut] = useState(() => consumeSignedOutFlag());
	const [state, setState] = useState<LoginState>("loading");
	const [email, setEmail] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [rememberedEmail, setRememberedEmail] = useState<string | null>(null);
	const [rememberedName, setRememberedName] = useState<string | null>(null);

	const idmRedirectUrl = environment.REACT_APP_REDIRECT_URL || "";

	useEffect(() => {
		if (isOnlyIdmAllowed && isSafeNavigationUrl(idmRedirectUrl)) {
			window.location.href = idmRedirectUrl;
		}
	}, [idmRedirectUrl]);

	useEffect(() => {
		if (!isCognitoLoginAllowed) {
			setState("fresh");
			return;
		}
		void resumePendingCognitoLogin();
		void initializeState();
	}, []);

	const initializeState = async () => {
		// State 1: active session — redirect into the app (closed-tab-and-reopened case)
		const session = await fetchAuthSession().catch(() => null);
		if (session?.tokens?.accessToken && !isSignedOut) {
			const basePath = (environment.REACT_APP_BASE_PATH || "").replace(/\/$/, "");
			window.location.href = `${basePath}/`;
			return;
		}

		// State 2 vs 3: check remembered user cookie
		const remembered = getRememberedUser();
		if (remembered) {
			setRememberedEmail(remembered.email);
			setRememberedName(remembered.name);
			setState("remembered");
		} else {
			setState("fresh");
		}
	};

	const handleEmailSubmit = useCallback(async () => {
		const trimmed = email.trim();
		if (!validateEmail(trimmed)) {
			setError(t("Please enter a valid email address"));
			return;
		}
		if (!isAllowedDomain(trimmed)) {
			setError(t("This email domain is not authorized to access this application"));
			return;
		}
		setError(null);

		const provider = resolveProviderForEmail(trimmed);
		await startInteractiveCognitoLogin(provider);
	}, [email, t]);

	const handleContinueAsRemembered = useCallback(async () => {
		if (!rememberedEmail) return;
		const provider = resolveProviderForEmail(rememberedEmail);
		await startInteractiveCognitoLogin(provider);
	}, [rememberedEmail]);

	const handleNotYou = useCallback(() => {
		clearRememberedUser();
		setRememberedEmail(null);
		setRememberedName(null);
		setState("fresh");
	}, []);

	const handleIdmLogin = async () => {
		if (!isSafeNavigationUrl(idmRedirectUrl)) {
			console.error("IDM redirect URL is missing or has an unsafe scheme");
			return;
		}
		await clearCognitoToken();
		window.location.href = idmRedirectUrl;
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			void handleEmailSubmit();
		}
	};

	if (state === "loading") {
		return (
			<div className="login-page scale-baseline-100">
				<div className="login-page__card" role="main">
					<div className="login-page__brand login-page__stagger login-page__stagger--1">
						<DynamicLogo redirectToHome={() => {}} />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="login-page scale-baseline-100">
			<div className="login-page__card" role="main">
				<div className="login-page__brand login-page__stagger login-page__stagger--1">
					<DynamicLogo redirectToHome={() => {}} />
				</div>

				<Typography.Title level={3} className="login-page__context-text login-page__stagger login-page__stagger--2">
					{isSignedOut ? t("Signed out") : t("Welcome back")}
				</Typography.Title>

				<Typography.Paragraph className="login-page__subtext login-page__stagger login-page__stagger--2">
					{isSignedOut
						? t("You have successfully signed out. Sign in again to continue.")
						: t("Sign in to your workspace")}
				</Typography.Paragraph>

				{/* State: Remembered User */}
				{state === "remembered" && rememberedEmail && (
					<>
						<Button
							htmlType="button"
							size="large"
							block
							onClick={handleContinueAsRemembered}
							className="login-page__btn-sso login-page__stagger login-page__stagger--3"
						>
							<i className="material-symbols-outlined login-page__btn-icon" aria-hidden="true">
								person
							</i>
							<span className="login-page__btn-text-truncate">
							{t("Continue as {{email}}", { email: rememberedEmail })}
						</span>
						</Button>
						<button
							type="button"
							onClick={handleNotYou}
							className="login-page__zs-link login-page__stagger login-page__stagger--4"
						>
							{t("Not you? Sign in with a different account")}
						</button>
					</>
				)}

				{/* State: Fresh Login */}
				{state === "fresh" && isCognitoLoginAllowed && (
					<>
						<div className="login-page__email-group login-page__stagger login-page__stagger--3">
							<Input
								size="large"
								type="email"
								placeholder={t("Enter your email address")}
								value={email}
								onChange={(e) => {
									setEmail(e.target.value);
									if (error) setError(null);
								}}
								onKeyDown={handleKeyDown}
								status={error ? "error" : undefined}
								className="login-page__email-input"
								autoFocus
							/>
							{error && (
								<Typography.Text type="danger" className="login-page__error-text">
									{error}
								</Typography.Text>
							)}
						</div>
						<Button
							htmlType="button"
							size="large"
							block
							onClick={handleEmailSubmit}
							className="login-page__btn-sso login-page__stagger login-page__stagger--4"
						>
							<i className="material-symbols-outlined login-page__btn-icon" aria-hidden="true">
								lock
							</i>
							{t("Continue")}
						</Button>
					</>
				)}

				{/* IDM fallback */}
				{state === "fresh" && isIdmLoginAllowed && (
					<Button
						htmlType="button"
						size="large"
						block
						onClick={handleIdmLogin}
						className="login-page__btn-sso login-page__stagger login-page__stagger--3"
					>
						{t("Login with IDM")}
					</Button>
				)}

			<div className="login-page__footer login-page__stagger login-page__stagger--5">
				<div className="login-page__footer-divider" role="separator" />
				<Typography.Paragraph className="login-page__footer-note">
					{t("Created by")}{" "}
					<a
						href="https://www.zs.com/"
						target="_blank"
						rel="noopener noreferrer"
						className="login-page__footer-link"
					>
						{t("ZS Associates")}
					</a>
				</Typography.Paragraph>
				<div className="login-page__legal-links">
					<a
						href="https://www.zs.com/compliance/zs-online-privacy-policy-for-hosted-applications"
						target="_blank"
						rel="noopener noreferrer"
						className="login-page__footer-link"
					>
						{t("Privacy Policy")}
					</a>
					<span className="login-page__legal-separator">|</span>
					<a
						href="https://www.zs.com/compliance/zs-terms-of-use-for-hosted-applications"
						target="_blank"
						rel="noopener noreferrer"
						className="login-page__footer-link"
					>
						{t("Terms of Use")}
					</a>
				</div>
			</div>
			</div>
		</div>
	);
};

export default Login;
